<template>
  <article class="advertising-item" :class="loading ? 'advertising-item--loading' : ''">
    <div class="advertising-item__preview">
      <img :src="`${useApi().baseUrl}${data.image}`" alt="广告图片">
    </div>

    <div class="advertising-item__content">
      <div class="advertising-item__header">
        <span class="advertising-item__badge">{{ data.type === 'weapp' ? '小程序' : '公众号文章' }}</span>
        <span v-if="data.appid" class="advertising-item__appid">appid: {{ data.appid }}</span>
      </div>
      <div class="advertising-item__url" :title="data.url">
        URL/Path：{{ data.url || '-' }}
      </div>
    </div>

    <div class="advertising-item__actions">
      <HpsfirButton v-if="!isH && !isFirst" variant="ghost" size="sm" @click="() => move('up')">
        <el-icon><ArrowUpBold /></el-icon> 前移
      </HpsfirButton>
      <HpsfirButton v-if="!isH && !isLast" variant="ghost" size="sm" @click="() => move('down')">
        <el-icon><ArrowDownBold /></el-icon> 后移
      </HpsfirButton>
      <HpsfirButton v-if="isH && !isFirst" variant="ghost" size="sm" @click="() => move('up')">
        <el-icon><ArrowLeftBold /></el-icon> 前移
      </HpsfirButton>
      <HpsfirButton v-if="isH && !isLast" variant="ghost" size="sm" @click="() => move('down')">
        <el-icon><ArrowRightBold /></el-icon> 后移
      </HpsfirButton>
      <button class="advertising-item__action-btn advertising-item__action-btn--danger" type="button" @click.stop="openDeleteDialog">
        <el-icon><Delete /></el-icon> 删除
      </button>
    </div>

    <HpsfirProDrawer
      v-model:open="deleteDialogVisible"
      title="删除广告"
      width="420px"
      max-width="calc(100vw - 32px)"
    >
      <div class="advertising-item__delete-confirm">
        <div class="advertising-item__delete-title">确定要删除这条广告吗？</div>
        <div class="advertising-item__delete-desc">删除后无法恢复，请谨慎操作。</div>
      </div>
      <template #footer>
        <HpsfirButton variant="outline" @click="deleteDialogVisible = false">取消</HpsfirButton>
        <HpsfirButton variant="destructive" :loading="loading" @click="confirmDelete">确定删除</HpsfirButton>
      </template>
    </HpsfirProDrawer>
  </article>
</template>

<script lang="ts" setup>
import { useApi } from '@/hooks/useApi';
import { useBaseAsync } from 'hpsfir-managebase-ui';
import { ref } from 'vue';
import { ElMessage } from 'element-plus';

const loading = ref(false);
const deleteDialogVisible = ref(false);

const props = defineProps<{
  data: any;
  isH?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
}>();

const emits = defineEmits(['change']);

function withTimeout<T>(promise: Promise<T>, message = '广告操作超时') {
  return Promise.race([
    promise,
    new Promise<T>((resolve, reject) => {
      void resolve;
      window.setTimeout(() => reject(new Error(message)), 15000);
    }),
  ]);
}

const move = useBaseAsync(async(type: 'up' | 'down') => {
  try {
    loading.value = true;
    await withTimeout(useApi().advertisingApi.moveAdvertisingadvertising({
      advertisingId: props.data.id,
      up: type === 'up',
    }));
    emits('change');
  } catch (error) {
    console.log(error);
    ElMessage.error(error instanceof Error ? error.message : '移动广告失败');
  } finally {
    loading.value = false;
  }
});

const openDeleteDialog = () => {
  deleteDialogVisible.value = true;
};

const confirmDelete = async() => {
  try {
    loading.value = true;
    await withTimeout(useApi().advertisingApi.delAdvertisingadvertising(props.data.id), '删除广告超时');
    deleteDialogVisible.value = false;
    ElMessage.success('删除成功');
    emits('change');
  } catch (error) {
    console.log(error);
    ElMessage.error(error instanceof Error ? error.message : '删除广告失败');
  } finally {
    loading.value = false;
  }
};
</script>

<style lang="scss">
.advertising-item {
  display: grid;
  grid-template-columns: minmax(120px, 180px) minmax(0, 1fr) auto;
  gap: 14px;
  align-items: center;
  min-height: 120px;
  padding: 14px;
  border: 1px solid color-mix(in srgb, var(--border, #e5e7eb) 76%, transparent);
  border-radius: 16px;
  background: color-mix(in srgb, var(--card, #fff) 96%, transparent);

  &--loading {
    opacity: 0.72;
  }

  &__preview {
    overflow: hidden;
    aspect-ratio: 16 / 9;
    border-radius: 12px;
    background: color-mix(in srgb, var(--muted, #f3f4f6) 45%, transparent);

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
  }

  &__content {
    min-width: 0;
  }

  &__header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    padding: 3px 10px;
    background: color-mix(in srgb, var(--primary, #111827) 10%, transparent);
    color: var(--primary, #111827);
    font-size: 12px;
    font-weight: 700;
  }

  &__appid {
    color: var(--muted-foreground, #6b7280);
    font-size: 12px;
  }

  &__url {
    overflow: hidden;
    color: var(--foreground, #111827);
    font-size: 13px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 6px;
    align-items: stretch;
  }

  &__action-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    min-height: 32px;
    padding: 0 12px;
    border: 1px solid color-mix(in srgb, var(--border, #d1d5db) 76%, transparent);
    border-radius: 8px;
    background: var(--card, #fff);
    color: var(--foreground, #111827);
    cursor: pointer;
    font-size: 13px;
    transition: all 0.16s ease;
  }

  &__action-btn--danger {
    border-color: color-mix(in srgb, #ef4444 36%, var(--border, #d1d5db));
    color: #dc2626;

    &:hover {
      background: color-mix(in srgb, #ef4444 10%, transparent);
      color: #b91c1c;
    }
  }

  &__delete-confirm {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 8px 0;
  }

  &__delete-title {
    color: var(--foreground, #111827);
    font-size: 16px;
    font-weight: 700;
  }

  &__delete-desc {
    color: var(--muted-foreground, #6b7280);
    font-size: 14px;
    line-height: 1.6;
  }
}

@media (max-width: 767px) {
  .advertising-item {
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 12px;

    &__actions {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      width: 100%;
    }

    &__actions > * {
      justify-content: center;
      min-width: 0;
    }
  }
}
</style>
